import React, { useEffect, useState } from 'react';
import {
  AlertTriangle, Archive, Boxes, ClipboardList, Gem, History, Menu, Recycle,
  Scale, LayoutDashboard, LogOut, ChevronRight,
} from 'lucide-react';
import { authApi, getStoredUser } from '../api';
import InventoryDashboard from './InventoryDashboard';
import MaterialMaster from './MaterialMaster';
import StoneInventory from './StoneInventory';
import BatchHistory from './BatchHistory';
import TransactionLedger from './TransactionLedger';
import MetalLossReport from './MetalLossReport';
import LowStockPanel from './LowStockPanel';
import ScrapPool from './ScrapPool';
import DispatchQueue from './DispatchQueue';
import OldStockBrowser from './OldStockBrowser';
import { canModule, hasAnyRole } from '../../utils/roleMatrix';
import ModuleGoalsDashboard from '../Goals/ModuleGoalsDashboard';

const INV_STYLES = `
  .inv-layout { display: flex; height: 100vh; overflow: hidden; font-family: 'Inter', sans-serif; }
  .inv-sidebar {
    width: 260px; min-width: 260px; background: linear-gradient(180deg,#1e1b4b 0%,#312e81 50%,#1e293b 100%);
    color: white; display: flex; flex-direction: column; transition: transform .3s ease; z-index: 20;
    box-shadow: 4px 0 24px rgba(0,0,0,.3);
  }
  .inv-main { flex: 1; display: flex; flex-direction: column; overflow: hidden; background: #f8fafc; }
  .inv-header {
    height: 64px; background: white; border-bottom: 1px solid #e2e8f0;
    display: flex; align-items: center; justify-content: space-between;
    padding: 0 24px; box-shadow: 0 1px 4px rgba(0,0,0,.06); z-index: 10; flex-shrink: 0;
  }
  .inv-content { flex: 1; overflow-y: auto; padding: 24px; background: #f8fafc; }
  .inv-content,
  .inv-content h1, .inv-content h2, .inv-content h3, .inv-content h4, .inv-content h5, .inv-content h6,
  .inv-content p, .inv-content span, .inv-content label, .inv-content small,
  .inv-content td, .inv-content th, .inv-content li, .inv-content a,
  .inv-content input, .inv-content textarea, .inv-content select {
    color: #000 !important;
  }
  .inv-content input::placeholder,
  .inv-content textarea::placeholder { color: #000 !important; opacity: 1; }
  .inv-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.4); z-index: 19; display: none; }
  .inv-overlay.visible { display: block; }
  .inv-nav-scroll { padding: 16px 12px; display: flex; flex-direction: column; gap: 4px; flex: 1; overflow-y: auto; }
  .inv-nav-label { font-size: 10px; font-weight: 700; color: #6366f1; text-transform: uppercase; letter-spacing: 1px; padding: 0 4px 8px; }
  .inv-nav-item {
    display: flex; align-items: center; gap: 12px; padding: 10px 16px; border-radius: 10px;
    cursor: pointer; transition: all .2s; border: none; background: transparent; color: #a5b4fc;
    font-size: 14px; font-weight: 500; width: 100%; text-align: left;
  }
  .inv-nav-item:hover { background: rgba(255,255,255,.08); color: white; }
  .inv-nav-item.active { background: rgba(255,255,255,.12); color: white; box-shadow: inset 0 0 0 1px rgba(255,255,255,.1); }
  .inv-badge {
    margin-left: auto; min-width: 20px; height: 20px; border-radius: 999px; padding: 0 6px;
    display: inline-flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700;
    background: rgba(239,68,68,.18); color: #fecaca;
  }
  @media(max-width:768px) {
    .inv-sidebar { position: fixed; height: 100%; transform: translateX(-100%); }
    .inv-sidebar.open { transform: translateX(0); }
    .inv-content { padding: 16px; }
  }
  @media(min-width:769px) { .inv-sidebar { position: relative; transform: none !important; } }
`;

const NAV_ITEMS = [
  { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { key: 'materials', label: 'Materials', icon: ClipboardList },
  { key: 'stones', label: 'Stone Inventory', icon: Gem },
  { key: 'batches', label: 'Batches', icon: Boxes },
  { key: 'transactions', label: 'Transactions', icon: History },
  { key: 'dispatchQueue', label: 'Dispatch Queue', icon: Boxes },
  { key: 'oldStock', label: 'Old Stock', icon: Archive },
  { key: 'scrapPool', label: 'Scrap Pool', icon: Recycle },
  { key: 'metalLoss', label: 'Metal Loss Report', icon: Scale },
  { key: 'lowStock', label: 'Low Stock', icon: AlertTriangle },
  { key: 'goals', label: 'Goals & Tasks', icon: ClipboardList },
];

function asText(value) {
  if (typeof value === 'string' || typeof value === 'number') return String(value).trim();
  if (value && typeof value === 'object') {
    return String(
      value.display_name ||
      value.name ||
      value.username ||
      value.role ||
      value.user_role ||
      value.message ||
      value.code ||
      ''
    ).trim();
  }
  return '';
}

const InventoryLayout = ({ user, onLogout }) => {
  const [active, setActive] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [lowStockCount, setLowStockCount] = useState(0);
  const isProductionWorker = hasAnyRole(user, ['PRODUCTION_WORKER']) && !canModule(user, 'inventory', 'create');
  const canEdit = canModule(user, 'inventory', 'create');
  const visibleNavItems = isProductionWorker
    ? NAV_ITEMS.filter((item) => ['materials', 'stones'].includes(item.key))
    : NAV_ITEMS;
  const storedUser = getStoredUser();
  const displayName = [
    [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim(),
    [storedUser?.first_name, storedUser?.last_name].filter(Boolean).join(' ').trim(),
    asText(user?.display_name),
    asText(storedUser?.display_name),
    asText(user?.name),
    asText(storedUser?.name),
    asText(user?.username),
    asText(storedUser?.username),
  ].find((value) => asText(value)) || 'Inventory User';
  const displayRole = [
    asText(user?.role),
    asText(user?.user_role),
    asText(storedUser?.role),
    asText(storedUser?.user_role),
  ].find((value) => asText(value)) || (canEdit ? 'Inventory Control' : 'Production View');

  useEffect(() => {
    authApi.get('/inventory/low-stock')
      .then((res) => setLowStockCount(res.data?.count || 0))
      .catch(() => setLowStockCount(0));
  }, []);

  useEffect(() => {
    const keys = visibleNavItems.map((item) => item.key);
    if (!keys.includes(active) && keys.length > 0) {
      setActive(keys[0]);
    }
  }, [active, visibleNavItems]);

  const renderScreen = () => {
    if (active === 'materials') return <MaterialMaster canEdit={canEdit} />;
    if (active === 'stones') return <StoneInventory />;
    if (active === 'batches') return <BatchHistory canEdit={canEdit} />;
    if (active === 'transactions') return <TransactionLedger />;
    if (active === 'dispatchQueue') return <DispatchQueue />;
    if (active === 'oldStock') return <OldStockBrowser />;
    if (active === 'scrapPool') return <ScrapPool />;
    if (active === 'metalLoss') return <MetalLossReport />;
    if (active === 'lowStock') return <LowStockPanel onCountChange={setLowStockCount} />;
    if (active === 'goals') return <ModuleGoalsDashboard moduleName="INVENTORY" />;
    return <InventoryDashboard />;
  };

  const getPageTitle = () => visibleNavItems.find((item) => item.key === active)?.label || 'Inventory';

  return (
    <>
      <style>{INV_STYLES}</style>
      <div className="inv-layout prod-module-root">
        <div className={`inv-overlay ${sidebarOpen ? 'visible' : ''}`} onClick={() => setSidebarOpen(false)} />

        <aside className={`inv-sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid rgba(255,255,255,.1)', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, background: 'linear-gradient(135deg,#f59e0b,#d97706)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Gem size={22} color="white" />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 900, letterSpacing: '-.5px', color: 'white' }}>
                ZILVERRA<span style={{ color: '#818cf8' }}>JEWELS</span>
              </div>
              <div style={{ fontSize: 11, color: '#a5b4fc' }}>Inventory Module</div>
            </div>
          </div>

          <div className="inv-nav-scroll">
            <div className="inv-nav-label">Inventory Hub</div>
            {visibleNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = active === item.key;
              return (
                <button
                  key={item.key}
                  className={`inv-nav-item ${isActive ? 'active' : ''}`}
                  onClick={() => {
                    setActive(item.key);
                    setSidebarOpen(false);
                  }}
                >
                  <Icon size={18} />
                  <span>{item.label}</span>
                  {item.key === 'lowStock' && lowStockCount > 0 && <span className="inv-badge">{lowStockCount}</span>}
                  {isActive && <ChevronRight size={14} style={{ marginLeft: 'auto' }} />}
                </button>
              );
            })}
          </div>

          <div style={{ margin: '12px', padding: '12px', background: 'rgba(0,0,0,.25)', borderRadius: 10, border: '1px solid rgba(255,255,255,.05)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: 'white' }}>
                {String(displayName || 'I').charAt(0).toUpperCase()}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'white' }}>{displayName}</div>
                <div style={{ fontSize: 11, color: '#a5b4fc' }}>{displayRole}</div>
              </div>
            </div>
          </div>
        </aside>

        <main className="inv-main">
          <header className="inv-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <button
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1a1a1a', padding: 4, display: 'flex', alignItems: 'center' }}
                onClick={() => setSidebarOpen((s) => !s)}
              >
                <Menu size={22} />
              </button>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: '#000', margin: 0 }}>{getPageTitle()}</h2>
            </div>
            <button
              onClick={() => window.confirm('Logout?') && onLogout?.()}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', border: '1px solid #fecaca', background: '#fff5f5', color: '#dc2626', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
            >
              <LogOut size={16} />
              Logout
            </button>
          </header>

          <div className="inv-content">
            {renderScreen()}
          </div>
        </main>
      </div>
    </>
  );
};

export default InventoryLayout;
